import { addError } from "@wayflyer/blackbox-fe";
import { type Action, FlyError, FlyErrorModalProvider, useFlyErrorModal } from "@wayflyer/flyui";
import { createContext, useCallback, useContext } from "react";
import { useIntl } from "react-intl";
import { getAppName } from "shared-utils";

import { type ErrorVariant, getCustomerErrorPresetMap, getStaffErrorPresetMap } from "./error-presets";

type ResolveActionsArgs = {
  defaultPrimaryAction?: Action;
  defaultSecondaryAction?: Action;
  primaryAction?: Action;
  secondaryAction?: Action;
  singleActionOnly?: true | "Primary";
};

function resolveActions({
  defaultPrimaryAction,
  defaultSecondaryAction,
  primaryAction,
  secondaryAction,
  singleActionOnly,
}: ResolveActionsArgs): { primaryAction?: Action; secondaryAction?: Action } {
  const resolvedPrimary = primaryAction ?? defaultPrimaryAction;

  // Single action with secondary styling (default): show one button in the secondary slot
  if (singleActionOnly === true) {
    return {
      primaryAction: undefined,
      secondaryAction: resolvedPrimary,
    };
  }

  // Single action with primary styling: show one button in the primary slot
  if (singleActionOnly === "Primary") {
    return {
      primaryAction: resolvedPrimary,
      secondaryAction: undefined,
    };
  }

  // Dual actions explicitly provided
  if (secondaryAction !== undefined) {
    return {
      primaryAction: resolvedPrimary,
      secondaryAction,
    };
  }

  // Defaults (respect singleActionOnly = "Primary" or undefined)
  return {
    primaryAction: resolvedPrimary,
    secondaryAction: singleActionOnly ? undefined : defaultSecondaryAction,
  };
}

type ErrorPreset = {
  /**
   * The error that will be logged
   */
  error: Error;
  /**
   * The variant of error to display, see frontend/packages/shared-ui/src/error-handling/error-variants.tsx
   */
  variant: ErrorVariant;
  /**
   * Override the default primary action for this variant
   */
  primaryAction?: Action;
  /**
   * Override the default secondary action for this variant
   */
  secondaryAction?: Action;
  /**
   * When there's only one action, specify button styling
   * - `true`: Use Secondary styling (recommended default)
   * - `"Primary"`: Use Primary styling
   */
  singleActionOnly?: true | "Primary";
};

export type ErrorModalContextValue = {
  /**
   * Opens an error modal and logs the error
   *
   * Pass a FlyError to display a custom error message, or
   * pass an ErrorPreset to display a predefined error message
   *
   * @example
   * // Custom error with FlyError
   * openErrorModal(new FlyError({
   *   title: "Custom Error",
   *   message: "Something went wrong",
   *   error: new Error("Details")
   * }));
   *
   * @example
   * // Preset error with single action (secondary styling - recommended)
   * openErrorModal({
   *   variant: "unknown",
   *   error: new Error("Info message"),
   *   singleActionOnly: true // Single action with secondary styling
   * });
   *
   * @example
   * // Preset error with single action (primary styling)
   * openErrorModal({
   *   variant: "submit-error",
   *   error: new Error("Request failed"),
   *   singleActionOnly: "Primary" // Use primary button styling
   * });
   */
  openErrorModal: (params: InstanceType<typeof FlyError> | ErrorPreset) => void;
  /**
   * Closes the error modal if it is open
   */
  closeErrorModal: () => void;
};

export interface AppErrorModalProviderProps {
  children: React.ReactNode;
}

const ProviderMissingError = new Error("Provider not found, components should be wrapped in AppErrorModalProvider");

export const ErrorModalContext = createContext<ErrorModalContextValue>({
  openErrorModal: () => {
    throw ProviderMissingError;
  },
  closeErrorModal: () => {
    throw ProviderMissingError;
  },
});

const ErrorModalContextWrapper = ({ children }) => {
  const intl = useIntl();
  const isCustomerFacing = getAppName() === "customer-app";
  const variantMap = isCustomerFacing ? getCustomerErrorPresetMap({ intl }) : getStaffErrorPresetMap();
  const { openErrorModal: openFlyErrorModal, closeErrorModal, errorModalIsOpen } = useFlyErrorModal();
  const openErrorModal = useCallback(
    (params: InstanceType<typeof FlyError> | ErrorPreset) => {
      if (errorModalIsOpen) {
        return;
      }

      if (params instanceof FlyError) {
        return openFlyErrorModal(params);
      }

      const { variant, primaryAction, secondaryAction, error, singleActionOnly } = params;

      // eslint-disable-next-line no-console
      !variantMap[variant] && console.warn(`Unknown error variant: ${variant}`);

      const errorVariant = variantMap[variant] ?? variantMap.unknown;
      const {
        title,
        message,
        primaryAction: defaultPrimaryAction,
        secondaryAction: defaultSecondaryAction,
      } = errorVariant;

      const { primaryAction: finalPrimaryAction, secondaryAction: finalSecondaryAction } = resolveActions({
        defaultPrimaryAction,
        defaultSecondaryAction,
        primaryAction,
        secondaryAction,
        singleActionOnly,
      });

      return openFlyErrorModal(
        new FlyError({
          title,
          message,
          primaryAction: finalPrimaryAction,
          secondaryAction: finalSecondaryAction,
          error,
        })
      );
    },
    [openFlyErrorModal, variantMap, errorModalIsOpen]
  );

  return children({ openErrorModal, closeErrorModal });
};

/**
 * Wraps FlyErrorModalProvider to add support for predefined / translated error messages
 */
export const AppErrorModalProvider = ({ children }: AppErrorModalProviderProps) => {
  return (
    <FlyErrorModalProvider logError={addError}>
      <ErrorModalContextWrapper>
        {({ openErrorModal, closeErrorModal }) => (
          <ErrorModalContext.Provider
            value={{
              openErrorModal,
              closeErrorModal,
            }}
          >
            {children}
          </ErrorModalContext.Provider>
        )}
      </ErrorModalContextWrapper>
    </FlyErrorModalProvider>
  );
};

/**
 * Wraps useFlyErrorModal to add support for preset error messages
 */
export const useErrorModal = (): ErrorModalContextValue => useContext(ErrorModalContext);
